import { useCallback, useDebugValue, useEffect, useMemo, useState } from 'react';

interface Result {
  loading: boolean;
  error: Pick<GeolocationPositionError, 'code' | 'message'> | null;
  coords: GeolocationPosition['coords'] | null;
  permission: 'unknown' | 'denied' | 'granted' | 'prompt';

  fetch: () => Promise<GeolocationPosition['coords']>;
}

export const useGeolocation = (): Result => {
  const [loading, setLoading] = useState(false);
  const [permission, setPermission] = useState<'unknown' | 'denied' | 'granted' | 'prompt'>('unknown');
  const [coordinates, setCoordinates] = useState<GeolocationPosition['coords'] | null>(null);
  const [errorCode, setErrorCode] = useState<GeolocationPositionError['code'] | null>(null);
  const [errorMessage, setErrorMessage] = useState<GeolocationPositionError['message'] | null>(null);

  useDebugValue(
    permission === 'granted' ? { latitude: coordinates?.latitude, longitude: coordinates?.longitude } : permission
  );

  // Active fetch geolocation
  const fetch = useCallback(() => {
    setLoading(true);

    return new Promise<GeolocationPosition['coords']>((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        ({ coords: newCoords }) => {
          setLoading(false);
          setCoordinates(newCoords);
          setErrorCode(null);
          setErrorMessage(null);
          resolve(newCoords);
        },
        (newError) => {
          setLoading(false);
          setErrorCode(newError.code);
          setErrorMessage(newError.message);
          reject(newError);
        }
      );
    });
  }, []);

  // Check permission
  useEffect(() => {
    if (!navigator.permissions) {
      // This is Safari
      return;
    }
    navigator.permissions.query({ name: 'geolocation' }).then((status) => {
      setPermission(status.state);
      status.onchange = function () {
        setPermission(this.state);
      };
    });
  }, []);

  // Honor Permission Change
  useEffect(() => {
    if (permission === 'granted') {
      navigator.geolocation.getCurrentPosition(
        ({ coords: newCoords }) => {
          setCoordinates(newCoords);
          setErrorCode(null);
          setErrorMessage(null);
        },
        (newError) => {
          setErrorCode(newError.code);
          setErrorMessage(newError.message);
        }
      );
    }
  }, [permission]);

  const error = useMemo(() => {
    if (errorCode && errorMessage) {
      return { code: errorCode, message: errorMessage };
    }
    return null;
  }, [errorMessage, errorCode]);

  return { fetch, loading, error, coords: coordinates, permission };
};
